import {
  filterClosePoints,
  getStrokeBounds,
  interpolatePointsUniform,
  translatePoints,
} from './helpers/points';
import { brushDefaults } from './helpers/brushLoader';

const defaultBrushSettings = brushDefaults.smudge || brushDefaults.blender || {};
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function normalizeVector(x, y) {
  const length = Math.hypot(x, y);
  if (length < 0.0001) return { x: 1, y: 0 };
  return { x: x / length, y: y / length };
}

function curveSmudgeStrength(value) {
  const normalized = clamp(value, 0, 1);
  const shifted = 0.3 + normalized;
  return Math.pow(shifted, 1.8);
}

function countTurnFactor(prev, curr, next) {
  if (!prev || !curr || !next) return 0;

  const inDir = normalizeVector(curr.x - prev.x, curr.y - prev.y);
  const outDir = normalizeVector(next.x - curr.x, next.y - curr.y);
  const dot = clamp(inDir.x * outDir.x + inDir.y * outDir.y, -1, 1);

  return clamp((1 - dot) * 0.5, 0, 1);
}

function thinSmudgePoints(points, minDistance) {
  if (!points || points.length < 3 || minDistance <= 0) {
    return points || [];
  }

  const baselinePoints = filterClosePoints(points, Math.max(1, minDistance * 0.55));
  if (baselinePoints.length < 3) {
    return baselinePoints;
  }

  const reducedPoints = [baselinePoints[0]];
  let lastKeptPoint = baselinePoints[0];

  for (let index = 1; index < baselinePoints.length - 1; index++) {
    const prev = baselinePoints[index - 1];
    const curr = baselinePoints[index];
    const next = baselinePoints[index + 1];

    const distanceFromLast = Math.hypot(curr.x - lastKeptPoint.x, curr.y - lastKeptPoint.y);
    const turnFactor = countTurnFactor(prev, curr, next);
    const adaptiveThreshold = minDistance * (1 - turnFactor * 0.5);

    if (distanceFromLast >= adaptiveThreshold) {
      reducedPoints.push(curr);
      lastKeptPoint = curr;
    }
  }

  const lastPoint = baselinePoints[baselinePoints.length - 1];
  if (reducedPoints[reducedPoints.length - 1] !== lastPoint) {
    reducedPoints.push(lastPoint);
  }

  return reducedPoints;
}

function copyRegionToCanvas(sourceCanvas, bounds) {
  const canvas = document.createElement('canvas');
  canvas.width = bounds.width;
  canvas.height = bounds.height;
  const ctx = canvas.getContext('2d');

  const srcX = Math.max(0, bounds.x);
  const srcY = Math.max(0, bounds.y);
  const dstX = Math.max(0, -bounds.x);
  const dstY = Math.max(0, -bounds.y);
  const drawWidth = Math.max(0, Math.min(sourceCanvas.width - srcX, bounds.width - dstX));
  const drawHeight = Math.max(0, Math.min(sourceCanvas.height - srcY, bounds.height - dstY));

  if (drawWidth > 0 && drawHeight > 0) {
    ctx.drawImage(
      sourceCanvas,
      srcX,
      srcY,
      drawWidth,
      drawHeight,
      dstX,
      dstY,
      drawWidth,
      drawHeight
    );
  }

  return canvas;
}

function clampRect(x, y, size, width, height) {
  const clampedX = clamp(x, 0, Math.max(0, width - size));
  const clampedY = clamp(y, 0, Math.max(0, height - size));
  return { x: clampedX, y: clampedY };
}

function createSoftMask(size) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const radius = size * 0.5;
  const gradient = ctx.createRadialGradient(
    radius,
    radius,
    size * 0.14,
    radius,
    radius,
    radius
  );
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.65, 'rgba(255,255,255,0.94)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return canvas;
}

function buildTransparencySample(targetCtx, sourceCanvas, tempCtx) {
  const size = targetCtx.canvas.width;
  targetCtx.clearRect(0, 0, size, size);
  targetCtx.globalCompositeOperation = 'source-over';
  targetCtx.fillStyle = '#ffffff';
  targetCtx.fillRect(0, 0, size, size);
  targetCtx.globalCompositeOperation = 'destination-out';
  targetCtx.drawImage(sourceCanvas, 0, 0);
  targetCtx.globalCompositeOperation = 'source-over';

  // Compress mid alpha aggressively so semi-transparent paint smears
  // instead of instantly turning into "eraser mass".
  tempCtx.clearRect(0, 0, size, size);
  tempCtx.globalCompositeOperation = 'source-over';
  tempCtx.drawImage(targetCtx.canvas, 0, 0);

  targetCtx.globalCompositeOperation = 'destination-in';
  targetCtx.drawImage(tempCtx.canvas, 0, 0);
  targetCtx.drawImage(tempCtx.canvas, 0, 0);
  targetCtx.globalCompositeOperation = 'source-over';
}

function captureSample(sampleCtx, sourceCanvas, x, y, sampleSize, boundsWidth, boundsHeight) {
  const sampleRect = clampRect(
    x - sampleSize * 0.5,
    y - sampleSize * 0.5,
    sampleSize,
    boundsWidth,
    boundsHeight
  );
  const targetSize = sampleCtx.canvas.width;

  sampleCtx.clearRect(0, 0, targetSize, targetSize);
  sampleCtx.drawImage(
    sourceCanvas,
    sampleRect.x,
    sampleRect.y,
    sampleSize,
    sampleSize,
    0,
    0,
    targetSize,
    targetSize
  );
}

function refreshMaskedCanvas(maskedCtx, sourceCanvas, maskCanvas) {
  const size = maskedCtx.canvas.width;
  maskedCtx.clearRect(0, 0, size, size);
  maskedCtx.globalCompositeOperation = 'source-over';
  maskedCtx.drawImage(sourceCanvas, 0, 0);
  maskedCtx.globalCompositeOperation = 'destination-in';
  maskedCtx.drawImage(maskCanvas, 0, 0);
  maskedCtx.globalCompositeOperation = 'source-over';
}

export async function drawBlenderStroke({
  lineWidth,
  points,
  sets = {},
}, context, params = {}) {
  if (!points || points.length < 2) return;

  const settings = Object.assign({}, defaultBrushSettings, sets);
  const {
    opacity = 0.28,
    flow = 0.6,
    blending = 0.72,
    softness = 0.58,
    density = 0.65,
    intensity = blending,
    dragLength = 1,
    pressureOn = true,
  } = settings;

  const sourceCtx = params.sourceCtx || context;
  const sourceCanvas = sourceCtx?.canvas;
  if (!sourceCanvas) return;

  const preview = !!params.preview;
  const rawIntensity = clamp(intensity, 0, 1);
  if (opacity <= 0) return;

  const effectiveIntensity = curveSmudgeStrength(rawIntensity);
  const effectiveDrag = clamp(flow * 0.15 + dragLength * 0.85, 0, 1.8);
  const padding = lineWidth * (1.05 + effectiveIntensity * 0.45 + effectiveDrag * 0.45);
  const bounds = getStrokeBounds(points, lineWidth * 2.1, padding, 48, 48);
  if (!bounds) return;

  const interpolatedPoints = interpolatePointsUniform(
    translatePoints(points, -bounds.x, -bounds.y),
    Math.max(1.4, lineWidth * (0.34 + (1 - clamp(density, 0, 1)) * 0.08))
  );
  const smudgeSpacing = Math.max(
    2.5,
    (0.3 + effectiveDrag * 0.12 + effectiveIntensity * 0.1) * 10
  );
  const localPoints = thinSmudgePoints(interpolatedPoints, smudgeSpacing);
  if (localPoints.length < 2) return;

  const sourceRegion = copyRegionToCanvas(sourceCanvas, bounds);

  const colorBufferCanvas = document.createElement('canvas');
  colorBufferCanvas.width = bounds.width;
  colorBufferCanvas.height = bounds.height;
  const colorBufferCtx = colorBufferCanvas.getContext('2d');
  colorBufferCtx.imageSmoothingEnabled = true;

  const eraseBufferCanvas = document.createElement('canvas');
  eraseBufferCanvas.width = bounds.width;
  eraseBufferCanvas.height = bounds.height;
  const eraseBufferCtx = eraseBufferCanvas.getContext('2d');
  eraseBufferCtx.imageSmoothingEnabled = true;

  const pickupResolution = clamp(
    Math.round(18 + lineWidth * (0.7 + effectiveIntensity * 0.18 + softness * 0.12)),
    18,
    preview ? 40 : 54
  );

  const sampleCanvas = document.createElement('canvas');
  sampleCanvas.width = pickupResolution;
  sampleCanvas.height = pickupResolution;
  const sampleCtx = sampleCanvas.getContext('2d');

  const transparencySampleCanvas = document.createElement('canvas');
  transparencySampleCanvas.width = pickupResolution;
  transparencySampleCanvas.height = pickupResolution;
  const transparencySampleCtx = transparencySampleCanvas.getContext('2d');

  const transparencyTempCanvas = document.createElement('canvas');
  transparencyTempCanvas.width = pickupResolution;
  transparencyTempCanvas.height = pickupResolution;
  const transparencyTempCtx = transparencyTempCanvas.getContext('2d');

  const pickupCanvas = document.createElement('canvas');
  pickupCanvas.width = pickupResolution;
  pickupCanvas.height = pickupResolution;
  const pickupCtx = pickupCanvas.getContext('2d');

  const clearPickupCanvas = document.createElement('canvas');
  clearPickupCanvas.width = pickupResolution;
  clearPickupCanvas.height = pickupResolution;
  const clearPickupCtx = clearPickupCanvas.getContext('2d');

  const maskedPickupCanvas = document.createElement('canvas');
  maskedPickupCanvas.width = pickupResolution;
  maskedPickupCanvas.height = pickupResolution;
  const maskedPickupCtx = maskedPickupCanvas.getContext('2d');

  const maskedClearCanvas = document.createElement('canvas');
  maskedClearCanvas.width = pickupResolution;
  maskedClearCanvas.height = pickupResolution;
  const maskedClearCtx = maskedClearCanvas.getContext('2d');

  const maskCanvas = createSoftMask(pickupResolution);

  const firstSampleSize = clamp(
    lineWidth * (0.68 + softness * 0.16),
    5,
    lineWidth * (1.08 + softness * 0.18 + effectiveIntensity * 0.16)
  );

  captureSample(
    sampleCtx,
    sourceRegion,
    localPoints[0].x,
    localPoints[0].y,
    firstSampleSize,
    bounds.width,
    bounds.height
  );
  buildTransparencySample(transparencySampleCtx, sampleCanvas, transparencyTempCtx);

  pickupCtx.clearRect(0, 0, pickupResolution, pickupResolution);
  pickupCtx.globalAlpha = 1;
  pickupCtx.drawImage(sampleCanvas, 0, 0);

  clearPickupCtx.clearRect(0, 0, pickupResolution, pickupResolution);
  clearPickupCtx.globalAlpha = 1;
  clearPickupCtx.drawImage(transparencySampleCanvas, 0, 0);

  refreshMaskedCanvas(maskedPickupCtx, pickupCanvas, maskCanvas);
  refreshMaskedCanvas(maskedClearCtx, clearPickupCanvas, maskCanvas);

  let hasErasePass = false;

  for (let index = 1; index < localPoints.length; index++) {
    const prev = localPoints[index - 1];
    const curr = localPoints[index];
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 0.01) continue;

    const direction = normalizeVector(dx, dy);
    const pressure = pressureOn ? (curr.pressure ?? prev.pressure ?? 1) : rawIntensity;
    const localRawIntensity = pressureOn ? clamp(pressure, 0, 1) : rawIntensity;
    const localEffectiveIntensity = pressureOn
      ? curveSmudgeStrength(localRawIntensity)
      : effectiveIntensity;
    const motionGain = clamp(dist / smudgeSpacing, 0.3, 1.2);
    const sampleSize = clamp(
      lineWidth * (0.68 + softness * 0.14),
      5,
      lineWidth * (1.06 + effectiveIntensity * 0.16 + effectiveDrag * 0.08)
    );

    captureSample(
      sampleCtx,
      sourceRegion,
      curr.x,
      curr.y,
      sampleSize,
      bounds.width,
      bounds.height
    );
    buildTransparencySample(transparencySampleCtx, sampleCanvas, transparencyTempCtx);

    const pickupRate = clamp(
      localEffectiveIntensity * 0.44 * motionGain,
      0,
      0.46
    );
    const dissipation = clamp(
      (0.032 + (1 - localEffectiveIntensity) * 0.075 + (1 - effectiveDrag / 1.8) * 0.02)
      / Math.max(motionGain, 0.38),
      0.028,
      0.2
    );
    const clearPickupRate = clamp(
      (0.3 + localEffectiveIntensity * 0.5) * motionGain,
      0,
      0.82
    );
    const transparentPickupDecay = clamp(
      0.12 + clearPickupRate * 0.82 + dissipation * 0.55,
      0.12,
      0.44
    );
    const clearDissipation = clamp(
      dissipation * (0.44 + (1 - localEffectiveIntensity) * 0.06),
      0.012,
      0.09
    );
    const clearReduction = clamp(
      pickupRate * 0.35,
      0,
      0.2
    );

    pickupCtx.save();
    pickupCtx.globalCompositeOperation = 'destination-out';
    pickupCtx.globalAlpha = dissipation;
    pickupCtx.fillRect(0, 0, pickupResolution, pickupResolution);
    pickupCtx.restore();

    pickupCtx.save();
    pickupCtx.globalCompositeOperation = 'source-over';
    pickupCtx.globalAlpha = pickupRate;
    pickupCtx.drawImage(sampleCanvas, 0, 0);
    pickupCtx.restore();

    // When we move through empty transparent space, the carried color
    // should gradually die out instead of repeating forever.
    pickupCtx.save();
    pickupCtx.globalCompositeOperation = 'destination-out';
    pickupCtx.globalAlpha = transparentPickupDecay;
    pickupCtx.drawImage(transparencySampleCanvas, 0, 0);
    pickupCtx.restore();

    clearPickupCtx.save();
    clearPickupCtx.globalCompositeOperation = 'destination-out';
    clearPickupCtx.globalAlpha = clearDissipation;
    clearPickupCtx.fillRect(0, 0, pickupResolution, pickupResolution);
    clearPickupCtx.restore();

    clearPickupCtx.save();
    clearPickupCtx.globalCompositeOperation = 'destination-out';
    clearPickupCtx.globalAlpha = clearReduction;
    clearPickupCtx.drawImage(sampleCanvas, 0, 0);
    clearPickupCtx.restore();

    clearPickupCtx.save();
    clearPickupCtx.globalCompositeOperation = 'source-over';
    clearPickupCtx.globalAlpha = clearPickupRate;
    clearPickupCtx.drawImage(transparencySampleCanvas, 0, 0);
    clearPickupCtx.restore();

    refreshMaskedCanvas(maskedPickupCtx, pickupCanvas, maskCanvas);
    refreshMaskedCanvas(maskedClearCtx, clearPickupCanvas, maskCanvas);

    const forwardDistance =
      (
        sampleSize * (0.015 + effectiveDrag * 0.16) +
        dist * (0.12 + effectiveDrag * 1.55)
      );
    const stampCount = clamp(
      Math.round(1 + forwardDistance / Math.max(5, lineWidth * 0.42)),
      1,
      preview ? 3 : 5
    );
    const baseAlpha = clamp(
      opacity
      * localEffectiveIntensity
      * (0.05 + motionGain * 0.17)
      * (0.72 + effectiveDrag * 0.16)
      * motionGain
      * (preview ? 0.92 : 1),
      0,
      0.22
    );
    const eraseBaseAlpha = clamp(
      baseAlpha
      * (0.82 + localEffectiveIntensity * 0.34)
      * (0.84 + effectiveDrag * 0.1),
      0,
      0.28
    );

    for (let stampIndex = 0; stampIndex < stampCount; stampIndex++) {
      const t = (stampIndex + 0.6) / (stampCount + 0.3);
      const centerX = curr.x + direction.x * forwardDistance * t;
      const centerY = curr.y + direction.y * forwardDistance * t;
      const stampWidth = sampleSize * (0.88 + softness * 0.16 - t * 0.18);
      const stampHeight = sampleSize * (0.9 + softness * 0.18 - t * 0.2);
      const alpha = baseAlpha * Math.pow(1 - t * 0.82, 1.55);
      const eraseAlpha = eraseBaseAlpha * Math.pow(1 - t * 0.72, 1.06);

      colorBufferCtx.globalAlpha = alpha;
      colorBufferCtx.drawImage(
        maskedPickupCanvas,
        centerX - stampWidth * 0.5,
        centerY - stampHeight * 0.5,
        stampWidth,
        stampHeight
      );

      colorBufferCtx.globalAlpha = alpha * clamp(0.48 + softness * 0.18, 0.38, 0.72);
      colorBufferCtx.drawImage(
        maskedPickupCanvas,
        centerX - stampWidth * 0.42,
        centerY - stampHeight * 0.42,
        stampWidth * 0.84,
        stampHeight * 0.84
      );

      const colorFadeAlpha = clamp(eraseAlpha * 0.42, 0, 0.13);
      if (colorFadeAlpha > 0.002) {
        colorBufferCtx.save();
        colorBufferCtx.globalCompositeOperation = 'destination-out';
        colorBufferCtx.globalAlpha = colorFadeAlpha;
        colorBufferCtx.drawImage(
          maskedClearCanvas,
          centerX - stampWidth * 0.48,
          centerY - stampHeight * 0.48,
          stampWidth * 0.96,
          stampHeight * 0.96
        );
        colorBufferCtx.globalAlpha = colorFadeAlpha * 0.6;
        colorBufferCtx.drawImage(
          maskedClearCanvas,
          centerX - stampWidth * 0.4,
          centerY - stampHeight * 0.4,
          stampWidth * 0.8,
          stampHeight * 0.8
        );
        colorBufferCtx.restore();
      }

      if (eraseAlpha > 0.003) {
        hasErasePass = true;
        eraseBufferCtx.globalAlpha = eraseAlpha;
        eraseBufferCtx.drawImage(
          maskedClearCanvas,
          centerX - stampWidth * 0.46,
          centerY - stampHeight * 0.46,
          stampWidth * 0.92,
          stampHeight * 0.92
        );

        eraseBufferCtx.globalAlpha = eraseAlpha * 0.58;
        eraseBufferCtx.drawImage(
          maskedClearCanvas,
          centerX - stampWidth * 0.38,
          centerY - stampHeight * 0.38,
          stampWidth * 0.76,
          stampHeight * 0.76
        );
      }
    }
  }

  if (hasErasePass) {
    context.save();
    context.globalCompositeOperation = 'destination-out';
    context.globalAlpha = 1;
    context.drawImage(
      eraseBufferCanvas,
      0,
      0,
      bounds.width,
      bounds.height,
      bounds.x,
      bounds.y,
      bounds.width,
      bounds.height
    );
    context.restore();
  }

  context.drawImage(
    colorBufferCanvas,
    0,
    0,
    bounds.width,
    bounds.height,
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height
  );
}
